/* One PDP gate; native product, navigation and cart controllers load normally. */
(() => {
  'use strict';
  if (window.FoolPdpSecondary) return;
  const selector = 'script[data-pdp-secondary][data-src]';
  const loads = new Map();
  const signals = ['scroll', 'touchstart', 'pointerdown', 'keydown'];
  let started = false, timer, chatPending = false;

  const idle = () => new Promise(resolve => {
    if ('requestIdleCallback' in window) requestIdleCallback(resolve, {timeout: 1000});
    else setTimeout(resolve, 32);
  });
  function load(node) {
    if (!node) return Promise.resolve();
    const src = node.dataset.src;
    if (loads.has(src)) return loads.get(src);
    const promise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      for (const attribute of node.attributes) {
        if (!['type', 'data-src', 'data-pdp-secondary'].includes(attribute.name)) {
          script.setAttribute(attribute.name, attribute.value);
        }
      }
      script.src = src;
      script.async = false;
      const fail = () => {
        clearTimeout(deadline);
        loads.delete(src);
        script.replaceWith(node);
        reject(new Error('Secondary script unavailable: ' + src));
      };
      const deadline = setTimeout(fail, 20000);
      script.onload = () => { clearTimeout(deadline); resolve(); };
      script.onerror = fail;
      // Replacing the inert tag preserves currentScript datasets and unique controllers.
      node.replaceWith(script);
    });
    loads.set(src, promise);
    return promise;
  }
  async function start() {
    if (started) return;
    started = true;
    clearTimeout(timer);
    signals.forEach(type => window.removeEventListener(type, onSignal));
    const groups = new Map();
    const immediate = [];
    for (const node of document.querySelectorAll(selector)) {
      const target = node.dataset.pdpNear;
      if (!target) immediate.push(node);
      else {
        if (!groups.has(target)) groups.set(target, []);
        groups.get(target).push(node);
      }
    }
    // Reviews are far below the purchase controls: hydrate when the reader approaches.
    for (const [targetSelector, nodes] of groups) {
      const targets = document.querySelectorAll(targetSelector);
      if (!targets.length) continue;
      let pending = false;
      const run = async () => {
        if (pending) return;
        pending = true;
        observer?.disconnect();
        for (const node of nodes) {
          await idle();
          try { await load(node); } catch (error) { console.warn(error.message); }
        }
      };
      const observer = 'IntersectionObserver' in window ? new IntersectionObserver(entries => {
        if (entries.some(entry => entry.isIntersecting)) void run();
      }, {rootMargin: '400px 0px'}) : null;
      if (observer) targets.forEach(target => observer.observe(target));
      else void run();
    }
    for (const node of immediate) {
      await idle();
      try { await load(node); } catch (error) { console.warn(error.message); }
    }
    window.dispatchEvent(new Event('fool:pdp-secondary-ready'));
  }
  function onSignal(event) { if (event.isTrusted) void start(); }
  async function onChat(event) {
    const root = document.querySelector('[data-shop-chat]');
    if (!window.FoolWidgets && !window.FoolShopChat && root?.classList.contains('has-load-error') && event.target.closest?.('[data-chat-close],[data-chat-minimize]')) {
      event.preventDefault(); event.stopImmediatePropagation();
      root.querySelector('.fool-chat__panel').hidden = true;
      root.classList.remove('has-load-error');
      const launcher = root.querySelector('[data-chat-toggle]');
      launcher.hidden = false; launcher.setAttribute('aria-expanded','false'); launcher.focus();
      return;
    }
    const trigger = event.target.closest?.('[data-chat-toggle],[data-mobile-chat-launcher],[data-support-chat],[data-chat-load-retry]');
    if (!trigger || window.FoolWidgets || window.FoolShopChat) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (chatPending) return;
    chatPending = true;
    trigger.setAttribute('aria-busy', 'true');
    try {
      await load(document.querySelector('script[data-pdp-secondary="noncritical-widgets.js"]'));
      await window.FoolWidgets?.openChat(trigger);
    } catch (error) {
      console.warn('Mira loader unavailable:', error.message);
      const fallback = document.querySelector('[data-shop-chat] [data-chat-load-fallback]');
      if (fallback && root) {
        root.classList.add('has-load-error');
        root.querySelector('.fool-chat__panel').hidden = false;
        const launcher = root.querySelector('[data-chat-toggle]');
        launcher.hidden = true; launcher.setAttribute('aria-expanded','true');
        fallback.hidden = false;
        root.querySelector('[data-chat-close]').focus({preventScroll:true});
      }
    } finally {
      chatPending = false;
      trigger.removeAttribute('aria-busy');
    }
  }
  function arm() {
    if (started) return;
    signals.forEach(type => window.addEventListener(type, onSignal, {passive: true}));
    // An idle callback's timeout is a deadline, not a minimum delay.
    timer = setTimeout(() => { void start(); }, 3500);
  }
  window.FoolPdpSecondary = {start};
  document.addEventListener('click', onChat, true);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', arm, {once: true});
  else arm();
  window.addEventListener('pagehide', () => {
    clearTimeout(timer);
    signals.forEach(type => window.removeEventListener(type, onSignal));
  });
  window.addEventListener('pageshow', event => { if (event.persisted) arm(); });
})();
